import React, { useState } from 'react';
import { RankingSnapshot, RankingWeightsConfig } from '../services/api';

interface ScoreBreakdownProps {
  ranking: RankingSnapshot;
  compact?: boolean;
  weights?: RankingWeightsConfig['weights'];
}

interface ScoreComponent {
  label: string;
  value: number;
  weight: number;
  normalized: number;
  color: string;
}

const DEFAULT_WEIGHTS = {
  revenue: 0.55, stock: 0.15, category: 0.08, brand: 0.07,
  potential_sales: 0.03, quantity: 0.10, price: 0.02,
};

const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ ranking, compact = true, weights }) => {
  const [viewMode, setViewMode] = useState<'weights' | 'contributions'>('weights');

  const w = weights || DEFAULT_WEIGHTS;

  // Definir componentes do score com pesos dinâmicos (ordem fixa para comparabilidade)
  const components: ScoreComponent[] = [
    {
      label: 'Rec', // Receita
      value: ranking.weighted_revenue || 0,
      weight: w.revenue,
      normalized: ranking.norm_revenue || 0,
      color: '#28a745' // verde
    },
    {
      label: 'Stk', // Stock
      value: ranking.weighted_stock || 0,
      weight: w.stock,
      normalized: ranking.norm_stock || 0,
      color: '#17a2b8' // cyan
    },
    {
      label: 'Cat', // Categoria
      value: ranking.weighted_category || 0,
      weight: w.category,
      normalized: ranking.norm_category || 0,
      color: '#ffc107' // amarelo
    },
    {
      label: 'Mar', // Marca
      value: ranking.weighted_brand || 0,
      weight: w.brand,
      normalized: ranking.norm_brand || 0,
      color: '#fd7e14' // laranja
    },
    {
      label: 'Pot', // Potencial
      value: ranking.weighted_potential || 0,
      weight: w.potential_sales,
      normalized: ranking.norm_potential || 0,
      color: '#6f42c1' // roxo
    },
    {
      label: 'Qtd', // Quantidade
      value: ranking.weighted_quantity || 0,
      weight: w.quantity,
      normalized: ranking.norm_quantity || 0,
      color: '#e83e8c' // rosa
    },
    {
      label: 'Prc', // Preço
      value: ranking.weighted_price || 0,
      weight: w.price,
      normalized: ranking.norm_price || 0,
      color: '#20c997' // teal
    }
  ];

  // Calcular benefícios e castigos (aplicados apenas à componente de receita)
  const penaltyFactor = ranking.penalty_applied || 1;
  const baseScore = ranking.base_score || 0;
  const weightedRevenue = ranking.weighted_revenue || 0;

  let benefitPoints = 0;
  let penaltyPoints = 0;

  if (penaltyFactor > 1) {
    benefitPoints = (penaltyFactor - 1) * weightedRevenue;
  } else if (penaltyFactor < 1) {
    penaltyPoints = (1 - penaltyFactor) * weightedRevenue;
  }

  const formatNumber = (value: number, decimals = 3): string => {
    return value.toFixed(decimals);
  };

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(0)}%`;
  };

  if (compact) {
    // Versão compacta para a tabela - duas visualizações lado a lado
    const totalScore = ranking.final_score || 0;

    return (
      <div className="score-breakdown-compact d-flex gap-2">
        {/* Primeira visualização: barras baseadas em valores normalized com pesos globais */}
        <div style={{ flex: 1 }}>
          <small className="text-muted" style={{ fontSize: '0.65rem' }}>Componentes (Pesos)</small>
          {components.map((comp, index) => {
            // Usar o valor normalizado (0 a 1) para a largura da barra
            const widthPercentage = comp.normalized * 100;
            const weightLabel = `${comp.label} (${(comp.weight * 100).toFixed(0)}%)`;
            
            return (
              <div key={`relative-${index}`} className="d-flex align-items-center" style={{ marginBottom: '1px' }}>
                <span className="text-muted small" style={{ width: '55px', fontSize: '0.65rem' }}>
                  {weightLabel}
                </span>
                <span className="small fw-bold" style={{ width: '38px', fontSize: '0.65rem', textAlign: 'right', marginRight: '3px' }}>
                  {formatNumber(comp.value)}
                </span>
                <div className="progress flex-grow-1" style={{ height: '10px' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${widthPercentage}%`,
                      backgroundColor: comp.color
                    }}
                    aria-valuenow={widthPercentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Divisor vertical */}
        <div style={{ width: '1px', backgroundColor: '#dee2e6' }}></div>
        
        {/* Segunda visualização: barras proporcionais ao score total */}
        <div style={{ flex: 1 }}>
          <small className="text-muted" style={{ fontSize: '0.65rem' }}>Contribuições p/ Score</small>
          {components.map((comp, index) => {
            const widthPercentage = totalScore > 0 ? (comp.value / totalScore * 100) : 0;
            
            return (
              <div key={`absolute-${index}`} className="d-flex align-items-center" style={{ marginBottom: '1px' }}>
                <span className="text-muted small" style={{ width: '28px', fontSize: '0.7rem' }}>
                  {comp.label}
                </span>
                <span className="small fw-bold" style={{ width: '45px', fontSize: '0.7rem', textAlign: 'right', marginRight: '4px' }}>
                  {formatNumber(comp.value)}
                </span>
                <div className="progress flex-grow-1" style={{ height: '10px' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${widthPercentage}%`,
                      backgroundColor: comp.color,
                      opacity: 0.8
                    }}
                    aria-valuenow={widthPercentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            );
          })}
          {/* Indicador de benefícios/castigos */}
          {penaltyFactor !== 1 && (() => {
            const adjustmentValue = penaltyFactor > 1 ? benefitPoints : penaltyPoints;
            const adjustmentPercentage = totalScore > 0 ? (adjustmentValue / totalScore * 100) : 0;
            const isBonus = penaltyFactor > 1;
            
            return (
              <div className="d-flex align-items-center" style={{ marginBottom: '1px' }}>
                <span className="text-muted small" style={{ width: '28px', fontSize: '0.7rem' }}>
                  {isBonus ? 'Ben' : 'Cas'}
                </span>
                <span className={`small fw-bold ${isBonus ? 'text-success' : 'text-danger'}`} style={{ width: '45px', fontSize: '0.7rem', textAlign: 'right', marginRight: '4px' }}>
                  {isBonus ? `+${formatNumber(adjustmentValue)}` : `-${formatNumber(adjustmentValue)}`}
                </span>
                <div className="progress flex-grow-1" style={{ height: '10px' }}>
                  <div
                    className={`progress-bar ${isBonus ? 'bg-success' : 'bg-danger'}`}
                    role="progressbar"
                    style={{
                      width: `${adjustmentPercentage}%`,
                      opacity: 0.8
                    }}
                    aria-valuenow={adjustmentPercentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Versão completa para o modal
  return (
    <div className="score-breakdown-full">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Decomposição do Score</h6>
        {/* Toggle entre visualizações */}
        <div className="btn-group btn-group-sm" role="group">
          <button
            type="button"
            className={`btn ${viewMode === 'weights' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('weights')}
          >
            Por Pesos
          </button>
          <button
            type="button"
            className={`btn ${viewMode === 'contributions' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('contributions')}
          >
            Por Contribuições
          </button>
        </div>
      </div>
      
      {/* Score total */}
      <div className="mb-4">
        <div className="d-flex justify-content-between mb-2">
          <span className="fw-bold">Score Base:</span>
          <span className="fw-bold">{formatNumber(ranking.base_score || 0, 4)}</span>
        </div>
        {ranking.penalty_applied !== 1 && (
          <>
            <div className="d-flex justify-content-between mb-2">
              <span>Fator de Ajuste (Receita):</span>
              <span className={ranking.penalty_applied! > 1 ? 'text-success' : 'text-danger'}>
                {ranking.penalty_applied! > 1 ? '+' : ''}{formatPercentage((ranking.penalty_applied! - 1))}
              </span>
            </div>
            <hr />
            <div className="d-flex justify-content-between">
              <span className="fw-bold">Score Final:</span>
              <span className="fw-bold">{formatNumber(ranking.final_score || 0, 4)}</span>
            </div>
          </>
        )}
      </div>

      {/* Componentes detalhados */}
      <h6 className="mb-3">
        {viewMode === 'weights' ? 'Componentes por Peso' : 'Contribuições para o Score'}
      </h6>
      
      {viewMode === 'weights' ? (
        // Visualização por pesos (original)
        <>
          {components.map((comp, index) => {
            const fullLabels: Record<string, string> = {
              'Rec': 'Receita',
              'Stk': 'Stock',
              'Cat': 'Categoria',
              'Mar': 'Marca',
              'Pot': 'Potencial',
              'Qtd': 'Quantidade',
              'Prc': 'Preço'
            };
            return (
              <div key={index} className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small">
                    {fullLabels[comp.label] || comp.label} ({formatPercentage(comp.weight)})
                  </span>
                  <span className="small text-muted">
                    Normalizado: {formatNumber(comp.normalized, 3)}
                  </span>
                </div>
                <div className="progress" style={{ height: '20px' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${comp.normalized * 100}%`,
                      backgroundColor: comp.color
                    }}
                    aria-valuenow={comp.normalized * 100}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <span className="small">
                      {formatNumber(comp.value, 3)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        // Nova visualização por contribuições
        <>
          {/* Calcular o score total uma vez para todos os componentes */}
          {(() => {
            const totalScore = ranking.final_score || 0;
            
            return (
              <>
                {/* Componentes base */}
                {components.map((comp, index) => {
                  const fullLabels: Record<string, string> = {
                    'Rec': 'Receita',
                    'Stk': 'Stock',
                    'Cat': 'Categoria',
                    'Mar': 'Marca',
                    'Pot': 'Potencial',
                    'Qtd': 'Quantidade',
                    'Prc': 'Preço'
                  };
                  // Calcular largura relativa ao score total
                  const widthPercentage = totalScore > 0 ? (comp.value / totalScore * 100) : 0;
                  
                  return (
                    <div key={index} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="small">
                          {fullLabels[comp.label] || comp.label}
                        </span>
                        <span className="small fw-bold">
                          +{formatNumber(comp.value, 4)}
                        </span>
                      </div>
                      <div className="progress" style={{ height: '20px' }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{
                            width: `${widthPercentage}%`,
                            backgroundColor: comp.color
                          }}
                          aria-valuenow={widthPercentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <span className="small">
                            {formatNumber(comp.value, 3)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Benefícios */}
                {benefitPoints > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="small text-success">
                  Benefícios
                </span>
                <span className="small fw-bold text-success">
                  +{formatNumber(benefitPoints, 4)}
                </span>
              </div>
              <div className="progress" style={{ height: '20px' }}>
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{
                    width: `${totalScore > 0 ? (benefitPoints / totalScore * 100) : 0}%`
                  }}
                  aria-valuenow={benefitPoints / totalScore * 100}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span className="small">
                    +{formatNumber(benefitPoints, 3)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Castigos */}
          {penaltyPoints > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="small text-danger">
                  Castigos
                </span>
                <span className="small fw-bold text-danger">
                  -{formatNumber(penaltyPoints, 4)}
                </span>
              </div>
              <div className="progress" style={{ height: '20px' }}>
                <div
                  className="progress-bar bg-danger"
                  role="progressbar"
                  style={{
                    width: `${totalScore > 0 ? (penaltyPoints / totalScore * 100) : 0}%`
                  }}
                  aria-valuenow={penaltyPoints / totalScore * 100}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span className="small">
                    -{formatNumber(penaltyPoints, 3)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
                {/* Total */}
                <hr />
                <div className="d-flex justify-content-between">
                  <span className="fw-bold">Total (Score Final):</span>
                  <span className="fw-bold">{formatNumber(ranking.final_score || 0, 4)}</span>
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* Valores brutos */}
      <div className="mt-4">
        <h6 className="mb-3">Valores Brutos</h6>
        <div className="row small">
          <div className="col-6">
            <div className="mb-2">
              <span className="text-muted">Receita 7d:</span><br />
              €{(ranking.revenue_total || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
            </div>
            <div className="mb-2">
              <span className="text-muted">Stock:</span><br />
              {(ranking.stock_quantity || 0).toLocaleString()} un
            </div>
            <div className="mb-2">
              <span className="text-muted">Quantidade 7d:</span><br />
              {(ranking.quantity_total || 0).toLocaleString()} un
            </div>
          </div>
          <div className="col-6">
            <div className="mb-2">
              <span className="text-muted">Receita Categoria:</span><br />
              €{(ranking.category_revenue || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
            </div>
            <div className="mb-2">
              <span className="text-muted">Receita Marca:</span><br />
              €{(ranking.brand_revenue || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
            </div>
            <div className="mb-2">
              <span className="text-muted">Preço Atual:</span><br />
              €{(ranking.preco_atual || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreBreakdown;